import React from 'react';

import styled from 'styled-components';
import { Text, type TextProps } from '@companycam/slab-web';

import ShimmerLoader from 'components/shared/ShimmerLoader/ShimmerLoader';
import SubmitPaymentButton from './SubmitPaymentButton';
import { tokens } from '@companycam/slab-tokens';
import {
  type PaymentMethodFormIdentifier,
  type SubscriptionOption,
} from '../types';
import { translate, type TranslationKey } from 'javascripts/translations/i18n';
import { ANNUAL } from '../Constants';

const SubmitContainer = styled.div`
  margin-top: var(--cc_size_spacing_l);
`;

const StyledTrialHeader = styled(Text)<TextProps>`
  span {
    font-weight: 700;
  }
`;

type SubmitPaymentProps = {
  billingLoading: boolean;
  handleSubmit: () => void;
  formId: PaymentMethodFormIdentifier;
  isWalletPayment: boolean;
  paymentSubmitLoading: boolean;
  planTitle: string;
  submitErrorMessage: string;
  submitPaymentDisabled: boolean;
  selectedSubscription: SubscriptionOption;
};

const SubmitPayment = ({
  billingLoading,
  selectedSubscription,
  handleSubmit,
  formId,
  isWalletPayment,
  paymentSubmitLoading,
  planTitle,
  submitErrorMessage,
  submitPaymentDisabled,
}: SubmitPaymentProps) => {
  const isAnnual = selectedSubscription?.cycle === ANNUAL;
  let priceCopy: TranslationKey =
    'billing.stripePaymentModal.timeline.timelineTrialInfoMonth';
  let price = parseInt(selectedSubscription?.baseMonthlyPrice);
  if (isAnnual) {
    priceCopy = 'billing.stripePaymentModal.timeline.timelineTrialInfoYear';
    price *= 12;
  }
  return (
    <SubmitContainer>
      {billingLoading ? (
        <ShimmerLoader height={tokens.size_text_l} />
      ) : (
        <StyledTrialHeader
          as="p"
          family="heading"
          size="xs"
          ccMargin="none none xxs none"
        >
          {translate(
            'billing.stripePaymentModal.timeline.timelineTrialInfoFirst',
          )}
          <span data-testid="choosePlans__cycle-price">
            {translate(priceCopy, {
              price,
            })}
          </span>
        </StyledTrialHeader>
      )}
      {billingLoading ? (
        <ShimmerLoader
          margin="0 0 var(--cc_size_spacing_s) 0"
          height={tokens.size_text_xl}
        />
      ) : (
        <Text
          as="p"
          size="xs"
          ccMargin="none none l none"
          data-testid="choosePlans__per-user-price"
        >
          {translate(
            'billing.stripePaymentModal.timeline.timelineTrialInfoSubHeader',
            {
              monthlyPricePerUser:
                selectedSubscription?.monthlyPricePerUser.split('.')[0],
            },
          )}
        </Text>
      )}
      <SubmitPaymentButton
        handleSubmit={handleSubmit}
        formId={formId}
        submitErrorMessage={submitErrorMessage}
        submitPaymentDisabled={submitPaymentDisabled}
        cta={translate('billing.stripePaymentModal.startTrial', {
          planTitle,
        })}
        dataContent="billing.stripePaymentModal.startTrial"
        isWalletPayment={isWalletPayment}
        paymentSubmitLoading={paymentSubmitLoading || !planTitle}
      />
    </SubmitContainer>
  );
};

export default SubmitPayment;
